<template>
	<div class="manage-amazon-affiliates">
		<h1>Manage Amazon Affiliates</h1>

		<!-- Add new affiliate form -->
		<div class="add-affiliate-form">
			<div class="form-grid">
				<div class="form-group">
					<label for="affiliateLink">Amazon Affiliate Link</label>
					<input
						id="affiliateLink"
						v-model="newAffiliate.link"
						type="text"
						placeholder="Enter Amazon affiliate link"
						class="form-input" />
				</div>

				<div class="form-group">
					<label for="affiliateTitle">Product Title</label>
					<input
						id="affiliateTitle"
						v-model="newAffiliate.title"
						type="text"
						placeholder="Enter product title"
						class="form-input" />
				</div>

				<div class="form-group">
					<label for="affiliateImage">Image URL</label>
					<input
						id="affiliateImage"
						v-model="newAffiliate.image"
						type="text"
						placeholder="Enter product image URL"
						class="form-input" />
				</div>

				<div class="form-group">
					<label for="affiliateRating">Rating (e.g., "500 ratings")</label>
					<input
						id="affiliateRating"
						v-model="newAffiliate.rating"
						type="text"
						placeholder="Enter product rating count"
						class="form-input" />
				</div>

				<div class="form-group">
					<label for="affiliateStars">Stars (e.g., "4.5 out of 5")</label>
					<input
						id="affiliateStars"
						v-model="newAffiliate.stars"
						type="text"
						placeholder="Enter product star rating"
						class="form-input" />
				</div>
			</div>

			<div class="form-actions">
				<button
					@click="addAffiliateLink"
					:disabled="isLoading || !isFormValid"
					class="add-button">
					{{ isLoading ? "Adding..." : "Add Affiliate" }}
				</button>
			</div>
		</div>

		<!-- Preview image if URL is provided -->
		<div v-if="newAffiliate.image" class="image-preview">
			<h3>Image Preview</h3>
			<img :src="newAffiliate.image" alt="Preview" class="preview-image" />
		</div>

		<!-- Affiliate links table -->
		<div class="affiliates-section">
			<h2>Current Affiliates</h2>
			<div class="affiliates-grid">
				<div
					v-for="affiliate in affiliates"
					:key="affiliate.id"
					class="affiliate-card"
					:class="{ inactive: !affiliate.is_active }">
					<div class="affiliate-image">
						<img :src="affiliate.image" :alt="affiliate.title" />
					</div>
					<div class="affiliate-content">
						<h3 :title="affiliate.title">
							{{ truncateText(affiliate.title, 70) }}
						</h3>
						<div class="affiliate-ratings">
							<span class="stars">{{ affiliate.stars }}</span>
							<span class="ratings">({{ affiliate.rating }})</span>
						</div>
						<div class="affiliate-actions">
							<button
								@click="toggleStatus(affiliate)"
								:class="[
									'status-button',
									affiliate.is_active ? 'active' : 'inactive',
								]">
								{{ affiliate.is_active ? "Active" : "Inactive" }}
							</button>
							<button @click="editAffiliate(affiliate)" class="edit-button">
								Edit
							</button>
							<button
								@click="deleteAffiliate(affiliate.id)"
								class="delete-button">
								Delete
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Edit Modal -->
		<div v-if="showEditModal" class="modal-overlay">
			<div class="modal-content">
				<h2>Edit Affiliate</h2>
				<div class="form-grid">
					<div class="form-group">
						<label for="editAffiliateLink">Amazon Affiliate Link</label>
						<input
							id="editAffiliateLink"
							v-model="editingAffiliate.link"
							type="text"
							class="form-input" />
					</div>

					<div class="form-group">
						<label for="editAffiliateTitle">Product Title</label>
						<input
							id="editAffiliateTitle"
							v-model="editingAffiliate.title"
							type="text"
							class="form-input" />
					</div>

					<div class="form-group">
						<label for="editAffiliateImage">Image URL</label>
						<input
							id="editAffiliateImage"
							v-model="editingAffiliate.image"
							type="text"
							class="form-input" />
					</div>

					<div class="form-group">
						<label for="editAffiliateRating">Rating</label>
						<input
							id="editAffiliateRating"
							v-model="editingAffiliate.rating"
							type="text"
							class="form-input" />
					</div>

					<div class="form-group">
						<label for="editAffiliateStars">Stars</label>
						<input
							id="editAffiliateStars"
							v-model="editingAffiliate.stars"
							type="text"
							class="form-input" />
					</div>
				</div>

				<div class="modal-actions">
					<button @click="updateAffiliate" class="save-button">Save</button>
					<button @click="cancelEdit" class="cancel-button">Cancel</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import { ref, computed, onMounted } from "vue"
	import { fetchWithAuth } from "@/utils/api-client"

	export default {
		name: "ManageAmazonAffiliates",
		setup() {
			const affiliates = ref([])
			const isLoading = ref(false)
			const showEditModal = ref(false)

			// New affiliate form data
			const newAffiliate = ref({
				link: "",
				title: "",
				image: "",
				rating: "",
				stars: "",
			})

			// Currently editing affiliate
			const editingAffiliate = ref({
				id: null,
				link: "",
				title: "",
				image: "",
				rating: "",
				stars: "",
				is_active: true,
			})

			// Function to truncate text
			const truncateText = (text, maxLength = 50) => {
				if (!text) return ""
				return text.length > maxLength
					? text.substring(0, maxLength) + "..."
					: text
			}

			// Validate form before submission
			const isFormValid = computed(() => {
				return (
					newAffiliate.value.link.trim() !== "" &&
					newAffiliate.value.title.trim() !== ""
				)
			})

			const fetchAffiliates = async () => {
				try {
					const response = await fetchWithAuth("/api/admin/amazon")
					if (!response.ok) throw new Error("Failed to fetch affiliates")
					affiliates.value = await response.json()
				} catch (error) {
					console.error("Error fetching affiliates:", error)
				}
			}

			const addAffiliateLink = async () => {
				if (!isFormValid.value) return

				isLoading.value = true
				try {
					const response = await fetchWithAuth("/api/admin/amazon", {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify(newAffiliate.value),
					})

					if (!response.ok) throw new Error("Failed to add affiliate")

					const data = await response.json()
					affiliates.value.push(data)

					// Reset form
					newAffiliate.value = {
						link: "",
						title: "",
						image: "",
						rating: "",
						stars: "",
					}
				} catch (error) {
					console.error("Error adding affiliate:", error)
				} finally {
					isLoading.value = false
				}
			}

			const toggleStatus = async (affiliate) => {
				try {
					const response = await fetchWithAuth(
						`/api/admin/amazon/${affiliate.id}`,
						{
							method: "PATCH",
							headers: {
								"Content-Type": "application/json",
							},
							body: JSON.stringify({
								is_active: !affiliate.is_active,
							}),
						}
					)

					if (!response.ok) throw new Error("Failed to update status")

					affiliate.is_active = !affiliate.is_active
				} catch (error) {
					console.error("Error toggling status:", error)
				}
			}

			const editAffiliate = (affiliate) => {
				editingAffiliate.value = { ...affiliate }
				showEditModal.value = true
			}

			const updateAffiliate = async () => {
				try {
					const response = await fetchWithAuth(
						`/api/admin/amazon/${editingAffiliate.value.id}`,
						{
							method: "PUT",
							headers: {
								"Content-Type": "application/json",
							},
							body: JSON.stringify(editingAffiliate.value),
						}
					)

					if (!response.ok) throw new Error("Failed to update affiliate")

					// Update affiliate in the list
					const index = affiliates.value.findIndex(
						(a) => a.id === editingAffiliate.value.id
					)
					if (index !== -1) {
						affiliates.value[index] = { ...editingAffiliate.value }
					}

					// Close modal
					showEditModal.value = false
				} catch (error) {
					console.error("Error updating affiliate:", error)
				}
			}

			const cancelEdit = () => {
				showEditModal.value = false
			}

			const deleteAffiliate = async (id) => {
				try {
					const response = await fetchWithAuth(`/api/admin/amazon/${id}`, {
						method: "DELETE",
					})

					if (!response.ok) throw new Error("Failed to delete affiliate")

					affiliates.value = affiliates.value.filter((a) => a.id !== id)
				} catch (error) {
					console.error("Error deleting affiliate:", error)
				}
			}

			onMounted(fetchAffiliates)

			return {
				affiliates,
				newAffiliate,
				editingAffiliate,
				isLoading,
				isFormValid,
				showEditModal,
				addAffiliateLink,
				toggleStatus,
				editAffiliate,
				updateAffiliate,
				cancelEdit,
				deleteAffiliate,
				truncateText,
			}
		},
	}
</script>

<style scoped>
	.manage-amazon-affiliates {
		padding: 1rem;
		max-width: 1200px;
		margin: 0 auto;
	}

	h1,
	h2 {
		margin-bottom: 1rem;
	}

	.form-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
		gap: 1rem;
		margin-bottom: 1rem;
	}

	.form-group {
		display: flex;
		flex-direction: column;
	}

	label {
		margin-bottom: 0.5rem;
		font-weight: bold;
	}

	.form-input {
		padding: 0.75rem;
		border: 1px solid #ddd;
		border-radius: 4px;
		font-size: 1rem;
	}

	.form-actions {
		margin-top: 1rem;
	}

	.add-button {
		padding: 0.75rem 1.5rem;
		background-color: #4caf50;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-size: 1rem;
	}

	.add-button:disabled {
		background-color: #cccccc;
		cursor: not-allowed;
	}

	.image-preview {
		margin: 1rem 0;
	}

	.preview-image {
		max-width: 200px;
		max-height: 200px;
		border: 1px solid #ddd;
		border-radius: 4px;
	}

	.affiliates-section {
		margin-top: 2rem;
	}

	.affiliates-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
		gap: 1.5rem;
		margin-top: 1rem;
	}

	.affiliate-card {
		border: 1px solid #ddd;
		border-radius: 8px;
		overflow: hidden;
		background: white;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
		transition: transform 0.2s, box-shadow 0.2s;
	}

	.affiliate-card:hover {
		transform: translateY(-2px);
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
	}

	.affiliate-card.inactive {
		opacity: 0.7;
	}

	.affiliate-image {
		position: relative;
		padding-top: 75%; /* 4:3 Aspect Ratio */
		background: #f5f5f5;
	}

	.affiliate-image img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: contain;
		padding: 0.5rem;
	}

	.affiliate-content {
		padding: 1rem;
	}

	.affiliate-content h3 {
		margin: 0 0 0.5rem 0;
		font-size: 1rem;
		line-height: 1.4;
		height: 2.8em;
		overflow: hidden;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
	}

	.affiliate-ratings {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		margin-bottom: 1rem;
		font-size: 0.9rem;
	}

	.stars {
		color: #f90;
		font-weight: bold;
	}

	.ratings {
		color: #666;
	}

	.affiliate-actions {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 0.5rem;
	}

	.affiliate-actions button {
		width: 100%;
		padding: 0.5rem;
		font-size: 0.8rem;
		white-space: nowrap;
	}

	/* Mobile responsiveness */
	@media (max-width: 768px) {
		.form-grid {
			grid-template-columns: 1fr;
		}

		.affiliates-grid {
			grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
			gap: 1rem;
		}

		.affiliate-content h3 {
			font-size: 0.9rem;
		}

		.affiliate-actions {
			grid-template-columns: 1fr;
		}

		.modal-content {
			padding: 1rem;
			width: 95%;
		}
	}

	/* Remove table styles as they're no longer needed */
	.table-responsive,
	table,
	th,
	td,
	.actions-cell {
		display: none;
	}

	/* Modal styles */
	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(0, 0, 0, 0.5);
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 1000;
	}

	.modal-content {
		background-color: white;
		padding: 2rem;
		border-radius: 8px;
		max-width: 90%;
		width: 600px;
		max-height: 90vh;
		overflow-y: auto;
	}

	.modal-actions {
		display: flex;
		justify-content: flex-end;
		gap: 1rem;
		margin-top: 1.5rem;
	}

	.save-button,
	.cancel-button {
		padding: 0.75rem 1.5rem;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-size: 1rem;
	}

	.save-button {
		background-color: #4caf50;
		color: white;
	}

	.cancel-button {
		background-color: #9e9e9e;
		color: white;
	}
</style>
